import React, { useCallback, useEffect, useState } from "react";
import './index.css';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { PlaceholderState } from "../../../../components/PlaceholderState";
import { usePreProductionPlan } from "../../../../hooks/usePreproductionPlanHook";
import {
  CustomMenu,
  QuantityMap,
  SetMenu,
} from "../../../../redux/allocationService";
import {
  useLazyGetPrePlanListQuery,
  useUpdatePreProductionPlanMutation,
} from "../../../../redux/services/allocationService";
import { CustomMenuSection } from "./CustomMenuSection";

interface PacksTabProps {
  quantities: QuantityMap;
  onQuantitiesChange: (quantities: QuantityMap) => void;
  selectedDate?: string;
}

const findMenuItemInCustomMenu = (
  customMenus: CustomMenu[],
  customMenuId: string,
  menuItemId: string,
): any => {
  const customMenu = customMenus.find((cm) => cm.itemId === customMenuId);
  if (!customMenu) return null;
  for (const section of customMenu.sections || []) {
    const menuItem = section.menuItems?.find((mi) => mi.itemId === menuItemId);
    if (menuItem) return menuItem;
  }
  return null;
};

export const PacksTab: React.FC<PacksTabProps> = ({
  quantities,
  onQuantitiesChange,
  selectedDate,
}) => {
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const [isSaving, setIsSaving] = useState(false);

  const [prePlanId, setPrePlanId] = useState<string | null>(null);

  const [fetchPrePlanList, { data, isLoading, isError, isFetching }] =
    useLazyGetPrePlanListQuery();

  const [updatePreProductionPlan] = useUpdatePreProductionPlanMutation();

  useEffect(() => {
    if (restaurantId) {
      fetchPrePlanList({ restaurantId });
    }
  }, [restaurantId, fetchPrePlanList]);

  const {
    plans,
    loading: plansLoading,
    error: plansError,
  } = usePreProductionPlan({
    restaurantId: restaurantId ?? "",
    targetDate: selectedDate,
    skip: !restaurantId,
  });

  useEffect(() => {
    if (!plans || plans.length === 0) {
      setPrePlanId(null);
      return;
    }

    setPrePlanId(plans[0].id);

    const aggregated: QuantityMap = {};

    for (const plan of plans) {
      for (const customMenu of plan.customMenus ?? []) {
        const packKey = `custom:${customMenu.itemId}`;
        aggregated[packKey] =
          (aggregated[packKey] ?? 0) + (customMenu.quantity ?? 0);

        for (const section of customMenu.sections ?? []) {
          for (const menuItem of section.menuItems ?? []) {
            const protKey = `prot:${customMenu.itemId}:${menuItem.itemId}`;
            aggregated[protKey] =
              (aggregated[protKey] ?? 0) + (menuItem.quantity ?? 0);
          }
        }
      }
    }

    if (Object.keys(aggregated).length > 0) {
      onQuantitiesChange(aggregated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  const handleQuantityChange = useCallback(
    (key: string, value: number) => {
      onQuantitiesChange({ ...quantities, [key]: value });
    },
    [quantities, onQuantitiesChange],
  );

  const handleRetry = useCallback(() => {
    if (restaurantId) {
      fetchPrePlanList({ restaurantId });
    }
  }, [restaurantId, fetchPrePlanList]);

  const buildPayload = useCallback(() => {
    const customMenusData: CustomMenu[] = data?.customMenus ?? [];
    const processedCustomMenus = new Map<string, any>();

    // Pack quantities
    for (const [key, newQuantity] of Object.entries(quantities)) {
      if (!key.startsWith("custom:")) continue;
      const customMenuId = key.replace("custom:", "");
      const quantity = newQuantity as number;
      const originalCustomMenu = customMenusData.find(
        (cm) => cm.itemId === customMenuId,
      );
      if (quantity > 0 && originalCustomMenu) {
        if (!processedCustomMenus.has(customMenuId)) {
          processedCustomMenus.set(customMenuId, {
            itemId: customMenuId,
            displayName: originalCustomMenu.displayName,
            price: originalCustomMenu.price,
            quantity: 0,
            skuId: originalCustomMenu.skuId ?? null,
            productId: originalCustomMenu.productId,
            sections: originalCustomMenu.sections
              ? JSON.parse(JSON.stringify(originalCustomMenu.sections))
              : [],
          });
        }
        processedCustomMenus.get(customMenuId)!.quantity = quantity;
      }
    }

    // Protein / menu-item quantities
    for (const [key, newCount] of Object.entries(quantities)) {
      if (!key.startsWith("prot:")) continue;
      const [, customMenuId, menuItemId] = key.split(":");
      const count = newCount as number;
      if (count <= 0) continue;

      if (!processedCustomMenus.has(customMenuId)) {
        const originalCustomMenu = customMenusData.find(
          (cm) => cm.itemId === customMenuId,
        );
        if (originalCustomMenu) {
          processedCustomMenus.set(customMenuId, {
            itemId: customMenuId,
            displayName: originalCustomMenu.displayName,
            price: originalCustomMenu.price,
            quantity: 0,
            skuId: originalCustomMenu.skuId ?? null,
            productId: originalCustomMenu.productId,
            sections: originalCustomMenu.sections
              ? JSON.parse(JSON.stringify(originalCustomMenu.sections))
              : [],
          });
        }
      }

      const customMenu = processedCustomMenus.get(customMenuId);
      if (!customMenu) continue;

      let found = false;
      for (const section of customMenu.sections) {
        const menuItem = section.menuItems?.find(
          (mi: any) => mi.itemId === menuItemId,
        );
        if (menuItem) {
          menuItem.quantity = count;
          found = true;
          break;
        }
      }

      if (!found) {
        if (!customMenu.menuItems) customMenu.menuItems = [];
        const originalMenuItem = findMenuItemInCustomMenu(
          customMenusData,
          customMenuId,
          menuItemId,
        );
        if (originalMenuItem) {
          const existingItem = customMenu.menuItems.find(
            (mi: any) => mi.itemId === menuItemId,
          );
          if (existingItem) {
            existingItem.quantity = count;
          } else {
            customMenu.menuItems.push({
              itemId: menuItemId,
              displayName: originalMenuItem.displayName,
              price: originalMenuItem.price,
              quantity: count,
              skuId: originalMenuItem.skuId ?? null,
              productId: originalMenuItem.productId,
            });
          }
        }
      }
    }

    const customMenus = Array.from(processedCustomMenus.values())
      .filter((cm) => {
        if (cm.quantity > 0) return true;
        return (
          cm.sections?.some((s: any) =>
            s.menuItems?.some((mi: any) => mi.quantity > 0),
          ) || cm.menuItems?.some((mi: any) => mi.quantity > 0)
        );
      })
      .map((cm) => ({
        ...cm,
        sections: (cm.sections || []).map((s: any) => ({
          ...s,
          menuItems: (s.menuItems || [])
            .filter((mi: any) => mi.quantity > 0)
            .map((mi: any) => ({ ...mi, skuId: mi.skuId ?? null })),
        })),
        ...(cm.menuItems && {
          menuItems: cm.menuItems
            .filter((mi: any) => mi.quantity > 0)
            .map((mi: any) => ({ ...mi, skuId: mi.skuId ?? null })),
        }),
      }));

    return {
      restaurantId,
      prePlanId,
      customMenus,
    };
  }, [restaurantId, prePlanId, quantities, data]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = buildPayload();

      console.log("=== SAVE PAYLOAD ===");
      console.log(JSON.stringify(payload, null, 2));
      console.log("===================");

      const result = await updatePreProductionPlan(payload).unwrap();

      toast.success(
        result.message || "Pre production plan saved successfully!",
      );
    } catch (error: any) {
      console.error("Save failed:", error);
      toast.error(
        error?.message ||
          "Failed to save pre production plan. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [buildPayload, updatePreProductionPlan]);

  const setMenus: SetMenu[] = data?.setMenus ?? [];
  const customMenus: CustomMenu[] = data?.customMenus ?? [];

  const isLoadingAny = isLoading || isFetching || plansLoading;

  if (isLoadingAny) {
    return (
      <PlaceholderState
        showSpinner
        title="Loading menus and order data..."
        subtitle="Please wait while we fetch your pack information"
      />
    );
  }

  if (isError || plansError) {
    return (
      <PlaceholderState
        icon="⚠️"
        title="Failed to load data"
        subtitle="Unable to fetch menus and order information. Please check your connection and try again."
        onAction={handleRetry}
        actionText="Retry"
        isError
      />
    );
  }

  if (!data) {
    return (
      <PlaceholderState
        icon="📋"
        title="No data loaded"
        subtitle="Waiting for restaurant data to load. Please ensure a restaurant is selected."
        onAction={handleRetry}
        actionText="Load Data"
      />
    );
  }

  if (setMenus.length === 0 && customMenus.length === 0) {
    return (
      <PlaceholderState
        icon="📦"
        title="No menus found"
        subtitle="No set menus or custom menus are configured for this restaurant. Please add some menus first."
        onAction={handleRetry}
        actionText="Refresh"
      />
    );
  }

  return (
    <div className="packs-tab-root">
      {/* Header with Save button */}
      <div
        className="packs-tab-header"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 10,
        }}
      >
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            padding: "8px 24px",
            backgroundColor: "#a43232",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: !isSaving ? "pointer" : "not-allowed",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isSaving ? (
            <>
              <span
                className="spinner"
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #fff",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Confirming...
            </>
          ) : (
            "Confirm Changes"
          )}
        </button>
      </div>

      <div className="packs-content">
        {customMenus.length > 0 && (
          <CustomMenuSection
            menus={customMenus}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
          />
        )}

        {/* Uncomment when SetMenuSection is needed:
        {setMenus.length > 0 && (
          <SetMenuSection
            menus={setMenus}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
          />
        )} */}
      </div>
    </div>
  );
};

export default PacksTab;
